<script module>
	/** Pentelion app has four opeartion modes
     * - public     show only the rules, powers and tutorial (panel shows index, main shows content)
	   * - authent    user has authenticated but not yet selected a mode
     * - preparing  edit adventures, sites and non-player characters
     * - managing   view and manage all heroes of the logged-in player
     * - playing    launch an own game or participate in a running game
     */

	import General from "./general/_General.svelte";
	import Prepare from ".//prepare/_Prepare.svelte"
  import Manage from ".//manage/_Manage.svelte";
  import Narrate from ".//narrate/_Narrate.svelte";
	import Play from ".//play/_Play.svelte";
	import Character from "./prepare/Character.svelte";
	import DeployDialog from "./dialogs/DeployDialog.svelte";
	import ActionMenu from "./dialogs/ActionMenu.svelte";
  import ItemEdit from "./dialogs/ItemEdit.svelte";
  import EffectDialog from "./dialogs/EffectDialog.svelte";
  import Inform from "./dialogs/Inform.svelte";
  import Exhibit from "./dialogs/Exhibit.svelte";
  import UpdateDocsDialog from "./dialogs/UpdateDocsDialog.svelte";
  import ScenesPanel from "./panels/ScenesPanel.svelte";
  import ImagePanel from "./panels/ImagePanel.svelte";
  import ControlPanel from "./control/Controller.svelte";
  import MasterPanel from "./control/Master.svelte";
  import PlotPanel from "./panels/PlotPanel.svelte";
  import NotesPanel from "./panels/NotesPanel.svelte";

	const screens = {
		public: General,
		advt: Prepare,
		hero: Manage,
		game: Narrate,
		play: Play, }


		const AvailDialogs = {
			character: 	Character,
			appearance: DeployDialog,   // (modal) on deploy map
			options:  	ActionMenu,     // (modal) on Encounter map
			effect:			EffectDialog,   // (modal) on Encounter map
			gearitem: 	ItemEdit,       // (modal) on GearSheet and from controller
			inform:  		Inform,         // (non-modal)  help from everywhere
			exhibit:		Exhibit,				// (non-modal)  view of an exhibit
			updatedocs: UpdateDocsDialog,   // (modal) admin-only: re-read documentation from Google Docs
		}

		export const AvailPanels = {
				stages:   ScenesPanel,
    		images:   ImagePanel,
    		control:  ControlPanel,
    		master:   MasterPanel,
				plot: 		PlotPanel,
				notes:		NotesPanel,
				exhibits: ImagePanel, }

		export const menuButtons =[
			{button: 'home', layout: 'home', icon: 'home', title: "Startseite", modes: ['public', 'authent', 'preparing', 'managing', 'playing']},
			{button: 'intro', layout: 'public-intro', icon: 'graduation-cap', title: "Einführung ins Rollenspiel", modes: ['public']},
			{button: 'features', icon: 'star', title: "Features", modes: ['public']},
			{button: 'advt', layout: 'advt', icon: 'advt', title: "Abenteuer vorbereiten", modes: ['authent', 'preparing', 'managing', 'playing']},
			{button: 'hero', layout: 'hero', icon: 'hero', title: "Helden verwalten", modes: ['authent', 'preparing', 'managing', 'playing']},
			{button: 'game', layout: 'game', icon: 'fshp', title: "Gruppen-Historie", modes: ['authent', 'preparing', 'managing', 'playing']},
			{button: 'play', layout: 'play', icon: 'play', title: "Zum Spiel zurück", modes: ['playing']},
			{button: 'leave', layout: 'exit', icon: 'hand', title: "Spiel verlassen", modes: ['playing']},
			{button: 'rules', layout: 'public-rules', icon: 'rules', title: "Regelbuch ansehen", modes: ['public', 'authent', 'preparing', 'managing', 'playing']},
			{button: 'logout', layout: 'logout', icon: 'right-from-bracket', title: "Ausloggen", modes: ['authent', 'preparing', 'managing', 'playing']} ]

</script>


<script>
    import { user, game, view } from "./client.svelte.js";
    import {protoActions, protoSkills} from "./core.js";
    import OptionDialog, {show_actionCard} from "./dialogs/OptionDialog.svelte";

		/** determines if Pentelion main menu is visible */
    let menuopen = $state(false)
		let Screen = $derived(screens[view.layout?.module])
    let DialogComponent = $derived(AvailDialogs[view.dialog?.type])
    let reloadTimer

    // $inspect(`APP - view.dialog`, view.dialog)


		let viewmode = $derived((game.ID) ? 'playing' : (view.adventure) ? 'preparing' : (user.ID) ? 'authent' : 'public')

		$effect(() => {
				if (user?.isMaster)
						document.body.classList.add('master')
				else
						document.body.classList.remove('master')
				if (view?.help)
						document.body.classList.add('helping')
				else
						document.body.classList.remove('helping')
		})


		function open_menu(e) {
				menuopen = true
				reloadTimer = setTimeout(() => reloadTimer = null, 200)
		}

		function close_menu(e) {
				menuopen = false
				console.log(`APP - Closing menu on ${e.target.tagName}`)
				if (reloadTimer && e.target.tagName === 'IMG')
						location.reload()
		}

		function switch_mode(mode) {
				// console.log(`APP - switch module to ${mode}`)
				if (mode === 'logout')
					user.logout()
				else if (mode === 'exit')
					game.terminate()
				else if (mode === 'manage' && user.heroes.length === 0)
					view.switch_layout('hero-creation')
				else
					view.switch_layout(mode)
				menuopen = false
		}

		function switch_tab() {
				// console.log(this, user.heroes)
				if (this === 'advt')
					user.unload_adventure()
				view.switch_layout(this)
		}

		function check_keystroke(e) {
        if (e.repeat) return;   // handle only the first key press

        // In the switch-case we're updating our boolean flags whenever the
        // desired bound keys are pressed.
        if (e.key === 'F1') {
	        e.preventDefault()
	        view.turnon_help(e)
        } else if (e.key === 'F12') {
						e.preventDefault()
		        user.commence_demo()
        } else if (e.key === 'ESC') {
						e.preventDefault()
        }
		}

		function show_topic(topic) {
				if (topic in protoSkills || topic in protoActions)
						show_actionCard(topic, 'map')
				else
					view.show_help(topic)
		}

		function change_hash(e) {
				e.preventDefault()
				const url = new URL(e.newURL)
				const topic = decodeURI(url.hash).split("#").at(-1)
				console.log(`APP - hash has changed to ${topic}`, url)
				show_topic(topic)
		}

		/// in-page anchors (<a href="#topic">) show their topic without adding '#' entries to the browser history
		function follow_anchor(e) {
				if (e.defaultPrevented || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return
				const anchor = e.target.closest?.('a[href^="#"]')
				if (!anchor || anchor.target) return
				e.preventDefault()
				const topic = decodeURIComponent(anchor.getAttribute('href').slice(1))
				console.log(`APP - anchor clicked for ${topic}`)
				if (topic) show_topic(topic)
		}

</script>


<!-------------------------------- HTML -------------------------------------->

{#if view.layout && view.layout.page !== 'home'}
  <nav id='mnav'>
	  <button id=homeBtn class=button title={view.layout.title}  onclick={open_menu} ondblclick={() => location.reload()} >
			<img src="/static/buttons/new/logo.png" alt="Logo" />
		  <span class="menu fa icon-bars"></span>
	  </button>
	  <div id=mainMenu class={menuopen ? "fade-in" : "fade-out"}>
      <div class="menu-open-header" onclick={close_menu}>
        <img class="logo" src="/static/buttons/new/logoText.png" alt="Logo"/>
        <div class="close fa icon-xmark"></div>
      </div>
      <menu>
        {#each menuButtons as item}
          {#if item.modes.includes(viewmode)}
            <li>
							<button class="mainMenu-item" onclick={() => switch_mode(item.layout)}>
								<div class="fa icon-{item.icon} mainMenu-item-logo"></div>
								<div class="menuTitle">{item.title}</div>
							</button>
           </li>
          {/if}
        {/each}
      </menu>
	  </div>
  </nav>
{/if}

<Screen />

{#if view.dialog?.type in AvailDialogs}
  <DialogComponent {...view.dialog.props} />
{/if}

{#if view.exhibit}
	<Exhibit/>
{/if}

{#if view.option && !('confirm' in view.option)}
	<OptionDialog />
{/if}

<svelte:window onkeydown={check_keystroke} onhashchange={change_hash} onclick={follow_anchor} />


<!-------------------------------- STYLES -------------------------------------->

<style lang="sass">
	@use "./colors" as colors		//depreciated: @import "../static/pentelion.sass"


	:global(.helping)
		--help-width: 360px

	/*** main navigation (dragon menu) ***/

	nav#mnav
		position: fixed
		left: 0
		top: 0
		z-index: 10

	button#homeBtn
		position: absolute     /*position: relative*/
		z-index: 10
		grid-row: 1 / span 3
		width: 115px
		height: 102px
		border: none
		background-color: colors.$primary-100
		padding: 0
		border-radius: 0 0 50px 0
		filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.8))
		color: colors.$white

	#homeBtn:hover
		cursor: pointer

	#homeBtn.hidden
		opacity: 0

	#homeBtn img
		width: 112px
		height: 101px
		margin-top: 1px
		margin-left: 1px

	#homeBtn .menu
		font-size: 24px
		position: absolute
		left: 60px
		top: 70px


	#mainMenu
		position: absolute     /*position: relative*/
		top: 0
		left: 0
		z-index: 15
		display: unset
		background-color: #3A3737
		font-weight: bold
		border-radius: 0 0 40px 0
		filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.8))

	#mainMenu ul
		list-style-type: none
		padding: 0
		margin-top: 30px
		margin-left: 35px

	#mainMenu .menu-open-header
		display: flex
		flex-flow: row nowrap
		background-color: colors.$primary-100
		width: 300px
		height: 103px
		justify-content: space-between

	#mainMenu .menu-open-header:hover
		cursor: pointer

	#mainMenu .menu-open-header .logo
		width: 250px
		height: 150px
		margin-left: 1px

	#mainMenu .menu-open-header .close
		font-size: 28px
		margin: 10px 10px 0 0
		color: colors.$white

	menu
		padding-inline-start: 12px

	.mainMenu-item
		display: flex
		flex-flow: row nowrap
		align-items: center
		padding: 20px 0 20px 25px
		height: 24px
		gap: 10px
		color: colors.$white
		background-color: colors.$grey-20
		border: none
		width: 100%

	.mainMenu-item-logo
		width: 40px
		font-size: 28px

	li:last-child .mainMenu-item
		padding: 20px 0 40px 25px
		width: calc(100% - 1px)

	li:first-child .mainMenu-item
		padding: 40px 0 20px 25px


	/* ----------- Menu fading in and out ------------ */

	#mainMenu
		transition-property: visibility, opacity
		/*transition-duration: .5s, .0s   change only .5s opacity duration */

	#mainMenu.fade-in
		-webkit-transition: opacity .3s linear
		transition: opacity .3s linear
		visibility: visible
		opacity: 1

	#mainMenu.fade-out
		-webkit-transition: visibility 0s linear .4s, opacity .3s ease-in-out
		transition: visibility 0s linear .4s, opacity .3s ease-in-out
		visibility: hidden
		opacity: 0


	/* ----------------------- */

	.mainMenu-item:hover
		cursor: pointer

	.mainMenu-item .menuTitle
		width: 100%
		border-bottom: 1px solid transparent
		text-align: left

	.mainMenu-item:hover .menuTitle
		border-bottom: 1px solid white

	.mainMenu-item img
		width: 24px
		height: 24px
		margin: 0 10px

	main
		height: 100vh
		overflow: hidden
		background: rgb(20 20 20)

	main.fullscreen
		left: 0
		width: 100vw

	.tabBar
		padding-left: 112px

	.tabBar img
		width: 44px

</style>